import { useApolloClient } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  FormErrorMessage,
  Grid,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useField } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { RiCloudLine } from "react-icons/ri";
import { INSERT_FORM_ANSWER } from "src/components/graphql/mutations";
import {
  GET_DOCUMENT_INFO,
  GET_FORM_ANSWER_ID,
} from "src/components/graphql/queries";
import { DocumentLibraryDialog } from "src/components/Inputs/DocumentLibraryDialog";
import { BaseInputProps } from "src/components/Inputs/QuestionDynamicInputs/Question";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  MAX_FILE_SIZE_IN_MB,
  MAX_NUMBER_OF_FILES,
  MB_SIZE,
  UPLOAD_FILE_STATE,
} from "src/constants";
import {
  ExistingDocument,
  useDocumentLibrary,
} from "src/hooks/useDocumentLibrary";
import { useDocumentStorage } from "src/hooks/useDocumentStorage";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import useUser from "src/hooks/useUser";
import { normalizeToAWSAcceptableKey } from "src/services/format";
import { FormikFieldValue } from "src/services/formTemplate/answer";
import { Status } from "src/types/authData";
import * as GQL from "src/types/graphql";
import { UploadFileInput } from "../../Inputs/UploadFileInput";
import { QuestionFileItem } from "./QuestionFileItem";
import { UploadFileItem } from "./UploadFileItem";

export interface UploadFileQuestionProps extends BaseInputProps {
  formId: string;
  disableDocumentLibrary?: boolean;
}

// Track upload state for each file
interface FileUploadState {
  file: File;
  progress: number;
  status: "uploading" | "success" | "error";
  errorMessage?: string;
  documentId?: string;
}

export const UploadFileQuestion: React.FC<UploadFileQuestionProps> = ({
  id,
  formId,
  isDisabled,
  disableDocumentLibrary = false,
}) => {
  const flags = useFlags(["document-library-parent"]);
  const toast = useToast();
  const [field, meta, helpers] = useField<FormikFieldValue>(id);
  const [uploadingFiles, setUploadingFiles] = useState<
    Map<string, FileUploadState>
  >(new Map());
  const [questionFiles, setQuestionFiles] = useState<string[] | undefined>();
  const [formAnswerId, setFormAnswerId] = useState<string | undefined>();
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [isReuploadingFile, setIsReuploadingFile] = useState<boolean>(false);

  const hasMaxNumberOfFiles =
    (questionFiles?.length ?? 0) >= MAX_NUMBER_OF_FILES;

  const user = useUser();
  const apolloClient = useApolloClient();
  const { getUploadUrl, uploadFile } = useDocumentStorage();

  // Seed the document-info query cache with data we already know at upload
  // time, so QuestionFileItem renders the filename instantly (no refetch).
  const primeDocumentInfoCache = useCallback(
    (documentId: string, filename: string, answerFormId: string) => {
      apolloClient.writeQuery<
        GQL.GetDocumentoInfo,
        GQL.GetDocumentoInfoVariables
      >({
        query: GET_DOCUMENT_INFO,
        variables: { document_id: documentId },
        data: {
          document_metadata_by_pk: {
            __typename: "document_metadata",
            document_id: documentId,
            filename,
            document_type: GQL.document_type_enum.General,
            form_answer_id: answerFormId,
          },
        },
      });
    },
    [apolloClient]
  );

  const { existingDocuments, refetchExistingDocuments, reuploadDocument } =
    useDocumentLibrary({
      userId: user.status === "ok" ? user.data.id : "",
      formAnswerId,
      formId,
      questionId: id,
      disabled: disableDocumentLibrary,
    });

  const [insertFormAnswer] = useRemoteDataMutation<
    GQL.InsertFormAnswerOne,
    GQL.InsertFormAnswerOneVariables
  >(INSERT_FORM_ANSWER);

  useRemoteDataQuery<GQL.GetFormAnswerId, GQL.GetFormAnswerIdVariables>(
    GET_FORM_ANSWER_ID,
    {
      variables: {
        question_id: id,
        form_id: formId,
      },
      onCompleted: (data) => {
        setFormAnswerId(data.form_answer[0]?.id);
      },
    }
  );

  useEffect(() => {
    if (Array.isArray(field.value)) {
      setQuestionFiles(field.value);
    }
  }, [field]);

  const ensureFormAnswerExists = useCallback(async (): Promise<string> => {
    if (formAnswerId) {
      return formAnswerId;
    }

    const result = await insertFormAnswer({
      variables: {
        form_answer: {
          form_id: formId,
          question_id: id,
        },
      },
    });

    const newFormAnswerId = result.data?.insert_form_answer_one?.id;
    if (!newFormAnswerId) {
      throw new Error("Failed to create form answer");
    }

    setFormAnswerId(newFormAnswerId);
    return newFormAnswerId;
  }, [formAnswerId, formId, id, insertFormAnswer]);

  const startFileUpload = useCallback(
    async (file: File) => {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

      try {
        // Check if file is too large
        if (file.size > MAX_FILE_SIZE_IN_MB * MB_SIZE) {
          setUploadingFiles((prev) =>
            new Map(prev).set(fileKey, {
              file,
              progress: 0,
              status: "error",
              errorMessage: UPLOAD_FILE_STATE.error.messages.FILE_TOO_LARGE,
            })
          );
          return;
        }

        // Check if user is authenticated
        if (user.status !== Status.OK) {
          setUploadingFiles((prev) =>
            new Map(prev).set(fileKey, {
              file,
              progress: 0,
              status: "error",
              errorMessage: "User not authenticated",
            })
          );
          return;
        }

        // Initialize upload state
        setUploadingFiles((prev) =>
          new Map(prev).set(fileKey, {
            file,
            progress: 0,
            status: "uploading",
          })
        );

        // Ensure form answer exists
        const currentFormAnswerId = await ensureFormAnswerExists();

        // Get upload URL
        const uploadUrl = await getUploadUrl({
          filename: file.name,
          document_content_type: file.type,
          document_type: GQL.document_type_enum.General,
          form_answer_id: currentFormAnswerId,
        });

        if (!uploadUrl) {
          throw new Error("Failed to get upload URL");
        }

        // Upload file
        const putResponse = await uploadFile(file, uploadUrl, (event) => {
          const progress = Math.round(
            (event.loaded * 100) / (event.total ?? 0)
          );
          setUploadingFiles((prev) => {
            const newMap = new Map(prev);
            const currentState = newMap.get(fileKey);
            if (currentState) {
              newMap.set(fileKey, {
                ...currentState,
                progress,
              });
            }
            return newMap;
          });
        });

        if (
          putResponse &&
          typeof putResponse === "object" &&
          "isAxiosError" in putResponse
        ) {
          throw new Error("Upload failed");
        }

        // Add to question files
        const newDocumentIds = questionFiles?.length
          ? [...questionFiles, uploadUrl.document_id]
          : [uploadUrl.document_id];

        primeDocumentInfoCache(
          uploadUrl.document_id,
          file.name,
          currentFormAnswerId
        );
        helpers.setValue(newDocumentIds);
        refetchExistingDocuments();

        // Remove from uploading files
        setUploadingFiles((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileKey);
          return newMap;
        });
      } catch (error) {
        console.error(error);
        setUploadingFiles((prev) =>
          new Map(prev).set(fileKey, {
            file,
            progress: 0,
            status: "error",
            errorMessage: UPLOAD_FILE_STATE.error.messages.UPLOAD_ERROR_DEFAULT,
          })
        );
      } finally {
        setIsUploadingFile(false);
      }
    },
    [
      user.status,
      ensureFormAnswerExists,
      getUploadUrl,
      uploadFile,
      questionFiles,
      primeDocumentInfoCache,
      helpers,
      refetchExistingDocuments,
    ]
  );

  const handleFileUpload = useCallback(
    (data: File) => {
      helpers.setTouched(true);

      // Normalize the filename once at the entry point; everything downstream
      // (display, state keys, upload URL request, S3 key) uses this name.
      const file = new File([data], normalizeToAWSAcceptableKey(data.name), {
        type: data.type,
        lastModified: data.lastModified,
      });

      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      if (uploadingFiles.has(fileKey)) {
        return; // Already uploading this file
      }

      setIsUploadingFile(true);
      startFileUpload(file);
    },
    [helpers, uploadingFiles, startFileUpload]
  );

  const handleRemoveFile = useCallback((file: File) => {
    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    setUploadingFiles((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileKey);
      return newMap;
    });
  }, []);

  const handleDeleteFile = useCallback(
    (documentId: string) => {
      const newDocumentIds = questionFiles?.filter(
        (questionFile) => questionFile !== documentId
      );
      helpers.setValue(newDocumentIds);
      refetchExistingDocuments();
    },
    [helpers, questionFiles, refetchExistingDocuments]
  );

  const {
    isOpen: isDocumentLibraryOpen,
    onOpen: openDocumentLibrary,
    onClose: closeDocumentLibrary,
  } = useDisclosure({ defaultIsOpen: false });

  return (
    <Box
      display="flex"
      flexDirection="column"
      onBlur={() => {
        helpers.setTouched(true);
      }}
    >
      <Flex direction="column" gap="2">
        {(questionFiles?.length || uploadingFiles.size > 0) && (
          <Flex direction="column">
            {questionFiles?.map((documentId) => (
              <QuestionFileItem
                key={documentId}
                documentId={documentId}
                onDeleteDocument={handleDeleteFile}
                viewMode={isDisabled}
              />
            ))}
            {Array.from(uploadingFiles.values()).map((uploadState) => (
              <UploadFileItem
                key={`${uploadState.file.name}-${uploadState.file.size}-${uploadState.file.lastModified}`}
                file={uploadState.file}
                progress={uploadState.progress}
                status={uploadState.status}
                errorMessage={uploadState.errorMessage}
                onRemoveFile={handleRemoveFile}
              />
            ))}
          </Flex>
        )}
        <Grid
          gap={2}
          gridTemplateColumns="repeat(auto-fit, minmax(12rem, 1fr))"
        >
          <UploadFileInput
            buttonLabel="Upload new file"
            onUpload={handleFileUpload}
            disabled={
              isDisabled ||
              isUploadingFile ||
              isReuploadingFile ||
              hasMaxNumberOfFiles
            }
          />
          {!disableDocumentLibrary &&
            flags["document-library-parent"].enabled &&
            existingDocuments.hasData() &&
            (existingDocuments.data.length > 0 ? (
              <>
                <DocumentLibraryDialog
                  isOpen={isDocumentLibraryOpen}
                  onClose={closeDocumentLibrary}
                  documents={existingDocuments.data}
                  onCancel={closeDocumentLibrary}
                  onUseFile={async (document: ExistingDocument) => {
                    setIsReuploadingFile(true);
                    try {
                      const resp = await reuploadDocument(document);
                      if (!formAnswerId) {
                        // this is a first file upload for this question.
                        // update the formAnswerId state so we can use the same id
                        // for the next file upload
                        setFormAnswerId(resp.formAnswerId);
                      }

                      const newDocumentIds = questionFiles?.length
                        ? [...questionFiles, resp.fileId]
                        : [resp.fileId];

                      primeDocumentInfoCache(
                        resp.fileId,
                        document.filename,
                        resp.formAnswerId
                      );
                      helpers.setValue(newDocumentIds);
                      refetchExistingDocuments();

                      closeDocumentLibrary();
                    } catch (error) {
                      console.error(error);
                      toast({
                        title: "Error adding file",
                        description:
                          "Please try again later or report the problem to our support team.",
                        status: "error",
                        isClosable: true,
                      });
                    } finally {
                      setIsReuploadingFile(false);
                    }
                  }}
                />
                <Button
                  alignSelf="center"
                  justifySelf="center"
                  leftIcon={<RiCloudLine />}
                  variant="outline"
                  colorScheme="gray"
                  width="100%"
                  whiteSpace="pre-wrap"
                  paddingY={2}
                  height="fit-content"
                  minHeight={10}
                  onClick={openDocumentLibrary}
                  isDisabled={
                    isDisabled ||
                    isUploadingFile ||
                    isReuploadingFile ||
                    hasMaxNumberOfFiles
                  }
                  isLoading={isReuploadingFile}
                >
                  Choose existing file
                </Button>
              </>
            ) : null)}
        </Grid>
      </Flex>
      {meta.touched && <FormErrorMessage>{meta.error}</FormErrorMessage>}
      <Text fontSize="xs" color="gray" mt={2}>
        {hasMaxNumberOfFiles ? (
          <>
            You've reached the maximum of <strong>{MAX_NUMBER_OF_FILES}</strong>{" "}
            files. Delete a file to upload a different one.
          </>
        ) : (
          <>
            You can upload <strong>{MAX_NUMBER_OF_FILES}</strong> files,
            supported formats: JPG/JPEG, PNG, HEIC, PDF.
          </>
        )}
        {window.screen.width < 1024 && " You can tap to preview the file."}
      </Text>
    </Box>
  );
};
